import React, { useCallback, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { mediaMax } from "components/designV2/breakpoints";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import { Link } from "react-router-dom";
import { useOnClickOutside } from "hooks/useOnClickOutside";

/** Item definition for the dropdown menu list. */
export interface DropdownMenuItem {
  avatarSrc?: string;
  href: string;
  iconName: string;
  isExpandable?: boolean;
  isSelected?: boolean;
  label: string;
  subItems?: DropdownMenuItem[];
  tone?: "default" | "danger";
  value: string;
}

/** Props for the dropdown menu component. */
export interface DropdownMenuProps {
  avatarSrc?: string;
  companyName: string;
  defaultOpen?: boolean;
  isOpen?: boolean;
  items: DropdownMenuItem[];
  onItemClick?: (
    item: DropdownMenuItem,
    event: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement
    >,
  ) => void;
  onToggle?: (open: boolean) => void;
}

const WIDTH = 248;
const HEADER_HEIGHT = 64;
const HEADER_RADIUS = 24;

const renderMenuItemContent = (item: DropdownMenuItem): React.ReactElement => {
  return (
    <MenuItemContent>
      <MenuItemLabel>{item.label}</MenuItemLabel>
    </MenuItemContent>
  );
};

/** Dropdown menu with optional avatar header and selectable items. */
export const DropdownMenu = (props: DropdownMenuProps): React.ReactElement => {
  const { avatarSrc, companyName, isOpen, items, onItemClick, onToggle } =
    props;
  const [internalOpen, setInternalOpen] = useState(props.defaultOpen ?? false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  const resolvedOpen = isOpen ?? internalOpen;
  const initials = useMemo((): string => {
    const parts = companyName.trim().split(/\s+/);
    const letters = parts
      .map((part) => part[0])
      .slice(0, 2)
      .join("");
    return letters.toUpperCase();
  }, [companyName]);

  const handleToggle = (): void => {
    const next = !resolvedOpen;
    if (isOpen === undefined) {
      setInternalOpen(next);
    }
    onToggle?.(next);
  };

  const closeMenu = useCallback((): void => {
    if (isOpen === undefined) {
      setInternalOpen(false);
    }
    onToggle?.(false);
  }, [isOpen, onToggle]);

  const handleItemClick = (
    item: DropdownMenuItem,
    event: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement
    >,
  ): void => {
    if (item.isExpandable) {
      event.preventDefault();
      setExpandedItems((prev) => ({
        ...prev,
        [item.value]: !prev[item.value],
      }));
      return;
    }

    onItemClick?.(item, event);
    closeMenu();
  };

  useOnClickOutside([wrapperRef], () => closeMenu(), {
    enabled: resolvedOpen,
  });

  return (
    <Wrapper ref={wrapperRef}>
      <Header type="button" onClick={handleToggle} $isOpen={resolvedOpen}>
        {avatarSrc ? (
          <Avatar src={avatarSrc} alt={`${companyName} logo`} />
        ) : (
          <AvatarFallback>{initials}</AvatarFallback>
        )}
        <CompanyName>{companyName}</CompanyName>
        <Chevron>
          <CustomIcon
            name={resolvedOpen ? "v2/up-chevron" : "v2/down-chevron"}
            size="24px"
            color={ColorV2.Blue5}
          />
        </Chevron>
      </Header>
      {resolvedOpen && (
        <Menu>
          {items.map((item) => (
            <React.Fragment key={item.value}>
              {item.isExpandable ? (
                <ExpandableMenuItem
                  type="button"
                  $tone={item.tone ?? "default"}
                  onClick={(event) => handleItemClick(item, event)}
                  aria-expanded={Boolean(expandedItems[item.value])}
                >
                  <IconContainer>
                    <CustomIcon
                      name={item.iconName}
                      size="24px"
                      color={
                        item.tone === "danger"
                          ? ColorV2.NegativeRed
                          : ColorV2.Blue1
                      }
                    />
                  </IconContainer>
                  {renderMenuItemContent(item)}
                  <CustomIcon
                    name={
                      expandedItems[item.value]
                        ? "v2/up-chevron"
                        : "v2/down-chevron"
                    }
                    size="20px"
                    color={ColorV2.Blue5}
                  />
                </ExpandableMenuItem>
              ) : (
                <MenuItemLink
                  to={item.href}
                  $tone={item.tone ?? "default"}
                  onClick={(event) => handleItemClick(item, event)}
                >
                  <IconContainer>
                    <CustomIcon
                      name={item.iconName}
                      size="24px"
                      color={
                        item.tone === "danger"
                          ? ColorV2.NegativeRed
                          : ColorV2.Blue1
                      }
                    />
                  </IconContainer>
                  {renderMenuItemContent(item)}
                </MenuItemLink>
              )}
              {item.isExpandable &&
                expandedItems[item.value] &&
                item.subItems?.map((subItem) => (
                  <SubMenuItemLink
                    key={subItem.value}
                    to={subItem.href}
                    $tone={subItem.tone ?? "default"}
                    $isSelected={Boolean(subItem.isSelected)}
                    onClick={(event) => handleItemClick(subItem, event)}
                  >
                    <SubIconContainer>
                      {subItem.avatarSrc ? (
                        <SubAvatar
                          src={subItem.avatarSrc}
                          alt={`${subItem.label} avatar`}
                        />
                      ) : (
                        <SubAvatarFallback />
                      )}
                    </SubIconContainer>
                    <MenuItemLabel>{subItem.label}</MenuItemLabel>
                  </SubMenuItemLink>
                ))}
            </React.Fragment>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: ${WIDTH}px;

  ${mediaMax.tablet} {
    width: auto;
  }
`;

const Header = styled.button<{ $isOpen: boolean }>`
  ${TypographyV2.b1}
  align-items: center;
  background-color: ${ColorV2.White};
  border: none;
  border-radius: ${(props) =>
    props.$isOpen
      ? `${HEADER_RADIUS}px ${HEADER_RADIUS}px 0 0`
      : `${HEADER_RADIUS}px`};
  cursor: pointer;
  display: flex;
  gap: 12px;
  height: ${HEADER_HEIGHT}px;
  padding: 8px 14px;
  position: relative;
  width: ${WIDTH}px;
  z-index: 2;

  ${mediaMax.tablet} {
    gap: 8px;
    padding: 8px 12px;
    width: auto;
  }
`;

const Avatar = styled.img`
  border-radius: 50%;
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

const AvatarFallback = styled.div`
  ${TypographyV2.b1}
  align-items: center;
  background-color: ${ColorV2.Blue6};
  border-radius: 50%;
  color: ${ColorV2.Blue1};
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const CompanyName = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  flex: 1;
  text-align: left;

  ${mediaMax.tablet} {
    display: none;
  }
`;

const Chevron = styled.span`
  align-items: center;
  display: inline-flex;
  justify-content: center;
`;

const Menu = styled.div`
  background-color: ${ColorV2.White};
  border-radius: 0 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  left: 0;
  padding: 16px 8px 12px;
  position: absolute;
  top: calc(100% - 8px);
  width: ${WIDTH}px;
  z-index: 1;

  ${mediaMax.tablet} {
    left: auto;
    right: 0;
  }
`;

const menuItemStyles = css<{ $tone: "default" | "danger" }>`
  ${TypographyV2.b1}
  align-items: center;
  border-radius: 12px;
  color: ${(props) =>
    props.$tone === "danger" ? ColorV2.NegativeRed : ColorV2.Blue1};
  cursor: pointer;
  display: flex;
  gap: 16px;
  min-height: 32px;
  padding: 8px 8px;
  text-decoration: none;
  width: 100%;

  &:hover {
    background-color: ${ColorV2.Blue7};
  }

  &:hover svg,
  &:hover svg * {
    color: ${(props) =>
      props.$tone === "danger" ? ColorV2.NegativeRed : ColorV2.Blue1};
  }

  ${(props) =>
    props.$tone === "danger" &&
    css`
      svg,
      svg * {
        color: ${ColorV2.NegativeRed};
      }
    `}
`;

const MenuItemLink = styled(Link)<{ $tone: "default" | "danger" }>`
  ${menuItemStyles}
`;

const ExpandableMenuItem = styled.button<{ $tone: "default" | "danger" }>`
  ${menuItemStyles}
  ${TypographyV2.b1}
  background: transparent;
  border: none;
  text-align: left;
`;

const SubMenuItemLink = styled(MenuItemLink)<{ $isSelected?: boolean }>`
  ${TypographyV2.s1}
  background-color: ${(props) =>
    props.$isSelected ? ColorV2.Blue1 : "transparent"};
  color: ${(props) => (props.$isSelected ? ColorV2.White : ColorV2.Blue1)};
  gap: 8px;
  padding-left: 16px;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected ? ColorV2.Blue1 : ColorV2.Blue6};
    color: ${(props) => (props.$isSelected ? ColorV2.White : ColorV2.Blue1)};
  }
`;

const MenuItemLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

const MenuItemContent = styled.span`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 8px;
  min-width: 0;
`;

const IconContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 24px;
`;

const SubIconContainer = styled(IconContainer)`
  margin-left: 8px;
`;

const SubAvatar = styled.img`
  border-radius: 50%;
  height: 24px;
  width: 24px;
`;

const SubAvatarFallback = styled.div`
  background-color: ${ColorV2.Blue6};
  border-radius: 50%;
  height: 24px;
  width: 24px;
`;
